// Orchestrator for the staff zone create/edit flow. Replaces the legacy
// 806-LOC src/pages/staff/ZoneForm.js. PR 12 ST 6 wires the composition;
// upload-image / generate-qr / regenerate-code / create / update all go
// through typed wrappers + React Query hooks. PR 13 deleted the
// auto-translate handler (and the underlying /exhibits/translate endpoint).
//
// Q1 option 1 per project_pr12_cadence: CRA's App.js continues to serve
// /staff/zone/*; only the import path flips to point here.

import { useMemo, useState, type FC, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import CodeSection from "@shared/components/CodeSection";
import { assetUrl } from "@shared/api/assets";
import { extractErrorMessage } from "@shared/api/errors";
import type { Zone, ZoneCreate, ZoneUpdate } from "@shared/api/zones";
import {
  useCreateZone,
  useGenerateZoneQR,
  useRegenerateZoneCode,
  useStaffZone,
  useUpdateZone,
  useUploadZoneImage,
} from "@shared/api/hooks/useZones";
import { useExhibitList } from "@shared/api/hooks/useExhibits";
import { useExhibitions } from "@shared/api/hooks/useExhibitions";

import Translations from "@staff/features/shared/Translations";
import ImageUpload from "@staff/features/shared/ImageUpload";
import LockdownBanner from "@staff/features/shared/LockdownBanner";
import { useEntityForm } from "@staff/features/shared/useEntityForm";
import ZoneMediaPanel from "@staff/features/zones/ZoneMediaPanel";
import QRSection from "@staff/features/shared/QRSection";
import PublishToggle from "@staff/features/shared/PublishToggle";
import ExhibitsList from "@staff/features/zones/ExhibitsList";

interface FormState {
  titleTc: string;
  titleEn: string;
  titleSc: string;
  descriptionTc: string;
  descriptionEn: string;
  descriptionSc: string;
  surveyUrl: string;
  exhibitionId: string;
}

const EMPTY_FORM: FormState = {
  titleTc: "",
  titleEn: "",
  titleSc: "",
  descriptionTc: "",
  descriptionEn: "",
  descriptionSc: "",
  surveyUrl: "",
  exhibitionId: "",
};

const ZoneForm: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [selectedExhibitIds, setSelectedExhibitIds] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [zoneCode, setZoneCode] = useState<string | null>(null);

  const { data: zone } = useStaffZone(id);
  const { data: allExhibits = [] } = useExhibitList();
  // #5: a zone can still reference purged exhibits (legacy multi-parent data).
  // They never appear in the selectable grid (list_all hides purged), so show
  // them read-only as a record of what the zone once contained.
  const { data: purgedExhibits = [] } = useExhibitList("purged");
  const purgedLinkedExhibits = useMemo(
    () => purgedExhibits.filter((e) => selectedExhibitIds.includes(e.id)),
    [purgedExhibits, selectedExhibitIds],
  );
  // F-C: include archived + purged parents so the dropdown still
  // resolves the current parent when this zone is cascade-archived.
  // Archived entries appear in the list with a tag; the parent-edit
  // path is gated server-side anyway (PATCH on archived zone → 409).
  const { data: exhibitions = [] } = useExhibitions({ includeArchived: true });
  const createZone = useCreateZone();
  const updateZone = useUpdateZone();
  const uploadImage = useUploadZoneImage();
  const generateQR = useGenerateZoneQR();
  const regenerateCode = useRegenerateZoneCode();

  // F-C: zones cascade-archived / cascade-purged by exhibition-level operations.
  // The shared engine owns the seed-once guard, lock derivation, code-locked
  // reason, unsaved-changes diff, and the per-language narration nudge (see
  // useEntityForm). Only the extra fields + auxiliary state are zone-specific.
  const {
    formData,
    patch,
    isPurged,
    isLocked,
    codeLockedReason,
    hasUnsavedChanges,
    narrationDescriptionChanged,
  } = useEntityForm<Zone, FormState>({
    entity: zone,
    isEdit,
    entityNoun: "zone",
    emptyForm: EMPTY_FORM,
    seedForm: (z) => ({
      titleTc: z.titleTc ?? "",
      titleEn: z.titleEn ?? "",
      titleSc: z.titleSc ?? "",
      descriptionTc: z.descriptionTc ?? "",
      descriptionEn: z.descriptionEn ?? "",
      descriptionSc: z.descriptionSc ?? "",
      surveyUrl: z.surveyUrl ?? "",
      exhibitionId: z.exhibitionId ?? "",
    }),
    onSeed: (z) => {
      setSelectedExhibitIds(z.exhibitIds ?? []);
      if (z.coverImageId) setImagePreview(assetUrl(z.coverImageId));
      if (z.qrCodeUrl) setQrCodeUrl(z.qrCodeUrl);
      if (z.code) setZoneCode(z.code);
    },
  });

  const toggleExhibit = (exhibitId: string) => {
    setSelectedExhibitIds((prev) =>
      prev.includes(exhibitId)
        ? prev.filter((eid) => eid !== exhibitId)
        : [...prev, exhibitId],
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.exhibitionId) {
      toast.error("Please pick an Exhibition for this zone.");
      return;
    }
    try {
      let savedId = id;
      if (isEdit && savedId) {
        // Skip the text PUT when only the cover image changed (2026-07-27,
        // approved) — an image-only save otherwise fires a no-op update
        // whose audit diff is empty. The upload below is its own request.
        if (hasUnsavedChanges || linksChanged) {
          await updateZone.mutateAsync({
            zoneId: savedId,
            payload: {
              ...formData,
              exhibitIds: selectedExhibitIds,
            } as ZoneUpdate,
          });
        }
        toast.success("Zone updated");
      } else {
        // ZoneCreate doesn't include exhibitIds — POST then PUT to attach.
        const created = await createZone.mutateAsync(formData as ZoneCreate);
        savedId = created.id;
        setZoneCode(created.code);
        if (selectedExhibitIds.length > 0) {
          await updateZone.mutateAsync({
            zoneId: savedId,
            payload: { exhibitIds: selectedExhibitIds } as ZoneUpdate,
          });
        }
        toast.success("Zone created");
      }
      if (imageFile && savedId) {
        await uploadImage.mutateAsync({ zoneId: savedId, file: imageFile });
        setImageFile(null);
      }
      // Update stays on the form (2026-07-24 UX request) — the update
      // mutation primes the detail cache, so the form re-syncs in place.
      // Create still returns to the zone dashboard.
      if (!isEdit) {
        navigate("/staff/zones");
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save zone"));
    }
  };

  const handleFileSelected = (file: File) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFileTooLarge = () => toast.error("Image must be less than 5MB");

  const handleGenerateQR = async () => {
    if (!id) {
      toast.error("Please save the zone first");
      return;
    }
    try {
      const res = await generateQR.mutateAsync(id);
      setQrCodeUrl(res.qrCodeUrl);
      toast.success("QR code generated");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to generate QR code"));
    }
  };

  const handleRegenerateCode = async () => {
    if (!id) {
      toast.error("Please save the zone first");
      return;
    }
    if (
      !window.confirm(
        "Regenerate zone code? This will change the code visitors use to access this zone.",
      )
    ) {
      return;
    }
    try {
      const res = await regenerateCode.mutateAsync(id);
      setZoneCode(res.code);
      toast.success(`New code generated: ${res.code}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to regenerate code"));
    }
  };

  const saving = createZone.isPending || updateZone.isPending;

  // Option (a), 2026-07-27: Update is disabled when nothing changed. Dirty =
  // text/exhibition fields (engine diff) OR a newly picked cover image OR a
  // changed exhibit-link selection (order-insensitive — the picker only
  // toggles membership).
  const linksChanged =
    isEdit &&
    [...selectedExhibitIds].sort().join(",") !==
      [...(zone?.exhibitIds ?? [])].sort().join(",");
  const dirty = hasUnsavedChanges || imageFile !== null || linksChanged;

  return (
    <div
      className="min-h-screen bg-background pb-12"
      data-testid="zone-form-page"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/staff/zones")}
              variant="ghost"
              size="sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-heading font-bold">
              {isEdit ? "Edit Zone" : "Create New Zone"}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {isLocked && (
            <LockdownBanner
              isPurged={isPurged}
              purged={
                <>
                  This zone has been <strong>purged</strong> via its parent
                  exhibition. Tombstone only; bytes removed.
                </>
              }
              archived={
                <>
                  This zone is <strong>archived</strong> (cascaded by its parent
                  exhibition). Restore the parent exhibition to bring it back
                  before editing.
                </>
              }
            />
          )}
          {!isLocked && (
            <ImageUpload
              label="Zone Image"
              previewUrl={imagePreview}
              onFileSelected={handleFileSelected}
              onFileTooLarge={handleFileTooLarge}
            />
          )}
          {isLocked && imagePreview && (
            <div className="space-y-2">
              <Label>Cover image</Label>
              <img
                src={imagePreview}
                alt="Cover"
                className="w-full max-w-md h-64 object-cover rounded-lg border border-border"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          )}

          <Translations
            formData={formData}
            onChange={patch}
            disabled={isLocked}
          />

          {/* Media sits right after title & description (2026-07-24 UX
              request) — the narration text is authored from those fields. */}
          {isEdit && id && zone && (
            <ZoneMediaPanel
              zone={zone}
              disabled={isLocked}
              unsavedChanges={hasUnsavedChanges}
              descriptionChanged={narrationDescriptionChanged}
            />
          )}

          {/* #1 + code: linkage grouped in one place — the visitor code, the
              parent exhibition, and the exhibits this zone contains, together
              after the descriptive fields. */}
          <div className="space-y-4" data-testid="placement-contents">
            <h2 className="text-lg font-heading font-semibold">
              Placement &amp; Contents
            </h2>
            <CodeSection
              code={zoneCode}
              label="Zone"
              entityNoun="zone"
              onRegenerate={isEdit ? handleRegenerateCode : undefined}
              regenerateDisabledReason={codeLockedReason}
            />
            {/* F-B: required parent Exhibition. Editing this is the
                "zone reassignment" path; server emits zone:reassigned
                audit and re-checks scope against both old + new parents. */}
            <fieldset className="border rounded p-4 space-y-3">
              <legend className="px-2 text-sm font-medium">
                Parent Exhibition
              </legend>
              <select
                value={formData.exhibitionId}
                onChange={(e) => patch({ exhibitionId: e.target.value })}
                className="w-full border rounded px-3 py-2"
                data-testid="exhibition-select"
                required
              >
                <option value="">— pick an exhibition —</option>
                {exhibitions.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {ex.titleTc}
                    {ex.titleEn && ex.titleEn !== ex.titleTc
                      ? ` / ${ex.titleEn}`
                      : ""}{" "}
                    ({ex.code})
                  </option>
                ))}
              </select>
              {isEdit &&
                zone &&
                zone.exhibitionId !== formData.exhibitionId && (
                  <p className="text-xs text-amber-700">
                    Changing this reassigns the zone. Editors must own the new
                    exhibition too, or the save will be rejected.
                  </p>
                )}
            </fieldset>
            <ExhibitsList
              allExhibits={allExhibits}
              selectedExhibitIds={selectedExhibitIds}
              onToggle={toggleExhibit}
              currentZoneId={id}
              purgedLinked={purgedLinkedExhibits}
            />
          </div>

          {isEdit && id && (
            <QRSection
              ownerNoun="zone"
              entityId={id}
              qrCodeUrl={qrCodeUrl}
              onGenerate={handleGenerateQR}
              generating={generateQR.isPending}
            />
          )}

          {/* Survey lives at the end (right before Save) per the form layout. */}
          <div
            className="space-y-4 p-6 bg-white border border-border rounded-lg"
            data-testid="survey-section"
          >
            <div>
              <h3 className="font-heading font-semibold">Survey</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Enter a URL to link a survey for this zone (e.g. Google Forms,
                Microsoft Forms). Shown to visitors after they finish exploring
                the zone.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="surveyUrl">URL</Label>
              <Input
                id="surveyUrl"
                value={formData.surveyUrl}
                onChange={(e) => patch({ surveyUrl: e.target.value })}
                disabled={isLocked}
                data-testid="survey-url-input"
              />
            </div>
          </div>

          {isEdit && id && <PublishToggle ownerKind="zone" entityId={id} />}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={saving || isLocked || (isEdit && !dirty)}
              title={isEdit && !dirty ? "No changes to save" : undefined}
              className="px-8"
              data-testid="save-zone-button"
            >
              {saving ? "Saving..." : isEdit ? "Update Zone" : "Create Zone"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/staff/zones")}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ZoneForm;
